import React, { useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { uploadToServer } from '../../services/uploadService';
import { db } from '../../firebase';
import { auth } from '../../firebase';

const ProfilePanel = ({ userData, formatFirestoreTimestamp, onProfileUpdate, showToast }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    fullName: userData?.fullName || '',
    phone: userData?.phone || '',
    cpf: userData?.cpf || '',
    category: userData?.category || '',
    profileImage: null
  });
  const [previewImage, setPreviewImage] = useState(userData?.profileImage || null);

  if (!userData) return null;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, profileImage: file }));
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  const handleSave = async () => {
    const user = auth.currentUser;
    if (!user) {
      if (showToast) showToast('Usuário não autenticado. Faça login novamente.', 'error');
      else alert('Usuário não autenticado. Faça login novamente.');
      return;
    }
    
    const userId = user.uid;
    
    setLoading(true);
    
    try {
      let profileImageUrl = userData.profileImage;

      if (formData.profileImage instanceof File) {
        console.log('Fazendo upload da imagem...');
        profileImageUrl = await uploadToServer(formData.profileImage, 'profiles');
        console.log('Upload concluído:', profileImageUrl);
      }

      const updateData = {
        fullName: formData.fullName.trim(),
        phone: formData.phone.trim(),
        cpf: formData.cpf.trim(),
        category: formData.category,
        updatedAt: new Date()
      };

      if (profileImageUrl && profileImageUrl !== userData.profileImage) {
        updateData.profileImage = profileImageUrl;
      }

      console.log('Dados para atualizar:', updateData);
      console.log('UID do usuário:', userId);

      await updateDoc(doc(db, 'users', userId), updateData);
      console.log('Perfil atualizado no Firebase');

      if (onProfileUpdate) {
        onProfileUpdate(updateData);
      }

      if (showToast) showToast('Perfil atualizado com sucesso!', 'success');
      else alert('Perfil atualizado com sucesso!');
      setIsEditing(false);
      
    } catch (error) {
      console.error('Erro completo ao atualizar perfil:', error);
      if (showToast) showToast(`Erro ao atualizar perfil: ${error.message}`, 'error');
      else alert(`Erro ao atualizar perfil: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      fullName: userData.fullName || '',
      phone: userData.phone || '',
      cpf: userData.cpf || '',
      category: userData.category || '',
      profileImage: null
    });
    setPreviewImage(userData.profileImage || null);
    setIsEditing(false);
  };

  const categories = [
    { value: 'loja-virtual', label: 'Loja Virtual' },
    { value: 'delivery', label: 'Delivery' },
    { value: 'sistema-agendamento', label: 'Sistema de Agendamento' },
    { value: 'catalogo', label: 'Catálogo' },
    { value: 'outro', label: 'Outro' }
  ];

  return (
    <div className="bg-white border-gray-200 dark:bg-gray-800 dark:border-gray-700 rounded-none shadow-sm border">
      {/* Header com Foto and Botão */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-gray-200 dark:from-blue-900/20 dark:to-indigo-900/20 dark:border-gray-600 px-4 sm:px-6 py-5 rounded-t-xl border-b">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center">
            <div className="relative flex-shrink-0">
              {previewImage ? (
                <img 
                  src={previewImage} 
                  alt="Profile" 
                  className="h-14 w-14 sm:h-16 sm:w-16 rounded-full object-cover border-4 border-white shadow-lg"
                />
              ) : (
                <div className="h-14 w-14 sm:h-16 sm:w-16 rounded-full bg-white dark:bg-gray-700 flex items-center justify-center border-4 border-white shadow-lg">
                  <span className="text-xl sm:text-2xl font-bold text-blue-600 dark:text-blue-400">
                    {userData.fullName?.charAt(0).toUpperCase() || 'U'}
                  </span>
                </div>
              )}
              {isEditing && (
                <label className="absolute bottom-0 right-0 bg-blue-600 text-white p-1 rounded-full cursor-pointer hover:bg-blue-700 transition-colors">
                  <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                </label>
              )}
            </div>
            
            <div className="ml-3 sm:ml-4 min-w-0 flex-1">
              {isEditing ? (
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  className="w-full text-lg sm:text-xl font-semibold bg-transparent border-b-2 border-blue-500 focus:outline-none focus:border-blue-600 px-1 py-1 text-gray-800 dark:text-white"
                  placeholder="Seu nome completo"
                />
              ) : (
                <>
                  <h1 className="text-lg sm:text-xl font-semibold truncate text-gray-800 dark:text-white">{userData.fullName || 'Usuário'}</h1>
                  <p className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm truncate">{userData.email}</p>
                </>
              )}
            </div>
          </div>

          <div className="flex space-x-2 sm:space-x-3 sm:flex-shrink-0">
            {isEditing ? (
              <>
                <button
                  onClick={handleCancel}
                  disabled={loading}
                  className="flex-1 sm:flex-none px-3 sm:px-4 py-2 text-gray-600 border-gray-300 hover:bg-gray-50 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700 border rounded-none text-xs sm:text-sm font-medium transition-colors disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-none text-xs sm:text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-3 w-3 sm:h-4 sm:w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className="hidden sm:inline">Salvando...</span>
                      <span className="sm:hidden">...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Salvar
                    </>
                  )}
                </button>
              </>
            ) : (
              <button 
                onClick={() => setIsEditing(true)}
                className="w-full sm:w-auto px-3 sm:px-4 py-2 bg-white text-blue-600 border-blue-200 hover:bg-blue-50 dark:bg-gray-700 dark:text-blue-400 dark:border-gray-600 dark:hover:bg-gray-600 border rounded-none text-xs sm:text-sm font-medium transition-colors flex items-center justify-center"
              >
                <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Editar Perfil
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Conteúdo Principal */}
      <div className="p-4 sm:p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Informações Pessoais */}
          <div className="space-y-6">
            <div className="bg-gray-50 dark:bg-gray-700 rounded-none p-5">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center">
                <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                Informações Pessoais
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Telefone</label>
                  {isEditing ? (
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border bg-white border-gray-300 text-gray-900 dark:bg-gray-600 dark:border-gray-500 dark:text-white rounded-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="(00) 00000-0000"
                    />
                  ) : (
                    <p className="text-gray-900 dark:text-white font-medium">{userData.phone || 'Não informado'}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">CPF</label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="cpf"
                      value={formData.cpf}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border bg-white border-gray-300 text-gray-900 dark:bg-gray-600 dark:border-gray-500 dark:text-white rounded-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="000.000.000-00"
                    />
                  ) : (
                    <p className="text-gray-900 dark:text-white font-medium">{userData.cpf || 'Não informado'}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Informações da Conta */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-none p-5">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center">
                <svg className="w-5 h-5 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                Informações da Conta
              </h3>
              
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Email</label>
                  <p className="text-gray-900 dark:text-white font-medium">{userData.email}</p>
                  <p className="text-xs text-gray-500 mt-1">Email não pode ser alterado</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Data de Cadastro</label>
                  <p className="text-gray-900 dark:text-white font-medium">
                    {userData.createdAt ? formatFirestoreTimestamp(userData.createdAt) : 'Data não disponível'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Categoria e Upload */}
          <div className="space-y-6">
            <div className="bg-gray-50 dark:bg-gray-700 rounded-none p-5">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center">
                <svg className="w-5 h-5 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
                Categoria
              </h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Categoria do Negócio</label>
                {isEditing ? (
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border bg-white border-gray-300 text-gray-900 dark:bg-gray-600 dark:border-gray-500 dark:text-white rounded-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Selecione uma categoria</option>
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="text-gray-900 dark:text-white font-medium">
                    {userData.category === 'loja-virtual' ? 'Loja Virtual' :
                     userData.category === 'delivery' ? 'Delivery' :
                     userData.category === 'sistema-agendamento' ? 'Sistema de Agendamento' :
                     userData.category === 'catalogo' ? 'Catálogo' :
                     userData.category === 'outro' ? 'Outro' : 'Não informado'}
                  </p>
                )}
              </div>
            </div>

            {/* Upload de Foto (apenas em modo edição) */}
            {isEditing && (
              <div className="bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800 rounded-none p-5 border">
                <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-300 mb-4 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Foto do Perfil
                </h3>
                
                <div className="text-center">
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-3">
                    Clique na foto ao lado para alterar ou escolha um arquivo:
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="block w-full text-sm text-gray-500 dark:text-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-blue-900/50 dark:file:text-blue-300 dark:hover:file:bg-blue-900"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Formatos suportados: JPG, PNG, GIF • Máx. 5MB
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Botões de Ação para Mobile */}
        {isEditing && (
          <div className="mt-8 pt-6 border-gray-200 dark:border-gray-600 border-t flex justify-end space-x-3 lg:hidden">
            <button
              onClick={handleCancel}
              disabled={loading}
              className="px-6 py-3 text-gray-600 border-gray-300 hover:bg-gray-50 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700 border rounded-none font-medium transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="px-6 py-3 bg-blue-600 text-white rounded-none font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {loading ? 'Salvando...' : 'Salvar Alterações'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfilePanel;